import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getBookings } from "../../services/booking.service";
import { getServices } from "../../services/inventory.service";
import { MdArrowBack, MdDownload, MdReceipt, MdAccessTime, MdCalendarToday, MdTimer, MdQrCode } from "react-icons/md";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getBusiness } from "../../services/auth.service";
import { AWS_URL } from "../../services/http.service";
import { useSelector } from "react-redux";
import { LogoLoading } from "../../components/common/utlity/utility";
import { getStorefrontPath } from "./storefrontPaths";
import { formatDate, formatDateTime } from "../../helpers/csshelper.js";

// Reuse the same function from BusinessDetailsQrPopup
const normalizeQrImage = (value) => {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `${AWS_URL}${value}`;
};

function ServiceInvoicePage() {
  const { businessName: urlBusinessName, bussinessUuid, bookingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [booking, setBooking] = useState(null);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const invoiceRef = useRef();
  const autoDownloadTriggeredRef = useRef(false);
  
  const businessData = useSelector((state) => state.bussiness.business);
  const business = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid) || businessData[0]
    : businessData;
  const activeStore = store || business || {};
  const displayBusinessName = urlBusinessName || activeStore.businessName || "Store";

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        
        // Get user profile from localStorage
        const userProfile = JSON.parse(
          localStorage.getItem(`user_${bussinessUuid}`),
        );
        
        // Also check for guest profile
        const guestProfile = JSON.parse(
          localStorage.getItem(`guest_${bussinessUuid}`),
        );
        
        const profile = userProfile || guestProfile;
        
        if (!profile) {
          setError("Please log in to view your invoice");
          setLoading(false);
          return;
        }

        // Fetch business/store details
        const result = await getBusiness(bussinessUuid);
        console.log("Business data from API:", result.data);
        
        if (result.data) {
          setStore(result.data);
          
          // Set QR code URL using the same logic as BusinessDetailsQrPopup
          const qrValue = result.data?.aadharPhoto || result.data?.bussinessUpiQr;
          console.log("QR value from business data:", qrValue);
          
          if (qrValue) {
            const normalizedUrl = normalizeQrImage(qrValue);
            console.log("Normalized QR URL:", normalizedUrl);
            setQrCodeUrl(normalizedUrl);
          } else {
            console.log("No QR code found in business data");
          }
        }

        // Fetch all bookings with filters
        const filters = {};
        
        // Try to find by customerId
        const customerId = profile?.customerId || profile?._id || profile?.id || "";
        if (customerId) {
          filters.customerId = customerId;
        }
        
        // Also try by mobile if available
        const mobile = profile?.customer_mobile || profile?.phone || profile?.mobile || "";
        if (mobile) {
          filters.mobile = mobile;
        }
        
        // Also try by email if available
        const email = profile?.email || "";
        if (email) {
          filters.email = email;
        }
        
        // Also try by name if available
        const name = profile?.name || profile?.customerName || "";
        if (name) {
          filters.name = name;
        }

        const response = await getBookings(bussinessUuid, filters);
        const bookingList = Array.isArray(response) ? response : [];

        // Try to find the booking by _id or bookingId
        let foundBooking = null;
        
        // First try exact match by _id
        foundBooking = bookingList.find((b) => b._id === bookingId);
        
        // If not found, try by bookingId field
        if (!foundBooking) {
          foundBooking = bookingList.find((b) => b.bookingId === bookingId);
        }
        
        // If still not found, try by matching any field that might contain the bookingId
        if (!foundBooking) {
          foundBooking = bookingList.find((b) => {
            const bId = b._id || b.bookingId || b.id || "";
            return String(bId) === String(bookingId);
          });
        }

        if (foundBooking) {
          console.log("Found booking:", foundBooking);
          
          // Try to fetch service details to get duration
          let duration = null;
          let serviceName = foundBooking.serviceName || foundBooking.service?.name || foundBooking.name || "Service";
          
          // First check if booking has serviceId or service
          const serviceId = foundBooking.serviceId || foundBooking.service?._id || foundBooking.service?.serviceId;
          
          if (serviceId) {
            try {
              console.log("Fetching service details for ID:", serviceId);
              const serviceResponse = await getServices(bussinessUuid, { serviceId: serviceId });
              console.log("Service response:", serviceResponse);
              
              let serviceData = null;
              if (serviceResponse?.data?.data && Array.isArray(serviceResponse.data.data)) {
                serviceData = serviceResponse.data.data[0];
              } else if (serviceResponse?.data && !Array.isArray(serviceResponse.data)) {
                serviceData = serviceResponse.data;
              } else if (Array.isArray(serviceResponse)) {
                serviceData = serviceResponse[0];
              }
              
              if (serviceData) {
                console.log("Found service data:", serviceData);
                setServiceDetails(serviceData);
                
                // Get duration from service data
                if (serviceData.serviceDuration) {
                  duration = serviceData.serviceDuration;
                } else if (serviceData.duration) {
                  duration = serviceData.duration;
                }
                
                // Get service name from service data if available
                if (serviceData.serviceName) {
                  serviceName = serviceData.serviceName;
                }
              }
            } catch (serviceError) {
              console.error("Error fetching service details:", serviceError);
            }
          }
          
          // If duration not found from service, check booking object
          if (!duration) {
            // Check all possible places where duration might be stored
            if (foundBooking.duration) {
              duration = foundBooking.duration;
            } else if (foundBooking.serviceDuration) {
              duration = foundBooking.serviceDuration;
            } else if (foundBooking.service?.duration) {
              duration = foundBooking.service.duration;
            } else if (foundBooking.service?.serviceDuration) {
              duration = foundBooking.service.serviceDuration;
            } else if (foundBooking.serviceDetails?.duration) {
              duration = foundBooking.serviceDetails.duration;
            } else if (foundBooking.serviceDetails?.serviceDuration) {
              duration = foundBooking.serviceDetails.serviceDuration;
            } else if (foundBooking.bookingDetails?.duration) {
              duration = foundBooking.bookingDetails.duration;
            } else if (foundBooking.bookingDetails?.serviceDuration) {
              duration = foundBooking.bookingDetails.serviceDuration;
            } else if (foundBooking.serviceData?.duration) {
              duration = foundBooking.serviceData.duration;
            } else if (foundBooking.serviceData?.serviceDuration) {
              duration = foundBooking.serviceData.serviceDuration;
            }
          }
          
          // Format the booking data
          const formattedBooking = {
            ...foundBooking,
            bookingDate: foundBooking.date || foundBooking.bookingDate || foundBooking.createdAt,
            bookingTime: foundBooking.time || foundBooking.bookingTime || "",
            duration: duration || "N/A",
            serviceName: serviceName,
          };
          
          console.log("Formatted booking with duration:", formattedBooking);
          
          setBooking(formattedBooking);
          setError(null);
        } else {
          console.error("Booking not found. Available bookings:", bookingList);
          setError("Booking not found. Please check if the booking ID is correct.");
        }
      } catch (err) {
        console.error("Error fetching booking:", err);
        setError(err.message || "Failed to fetch booking details");
      } finally {
        setLoading(false);
      }
    };

    if (bussinessUuid && bookingId) {
      fetchBooking();
    } else {
      setError("Missing booking information");
      setLoading(false);
    }
  }, [bussinessUuid, bookingId]);

  const searchParams = new URLSearchParams(location.search);
  const shouldAutoDownload = searchParams.get("download") === "pdf";
  const closeAfterDownload = searchParams.get("closeAfterDownload") === "1";

  const downloadInvoice = async (options = {}) => {
    setDownloading(true);
    try {
      const element = invoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`service-invoice-${booking.bookingId || booking._id}.pdf`);

      if (options.closeAfterDownload && window.opener) {
        window.close();
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!booking || !shouldAutoDownload || autoDownloadTriggeredRef.current) {
      return;
    }

    autoDownloadTriggeredRef.current = true;
    downloadInvoice({ closeAfterDownload });
  }, [booking, shouldAutoDownload, closeAfterDownload]);

  if (loading) {
    return <LogoLoading />;
  }

  if (error) {
    return (
      <div className='min-h-screen bg-gray-100 flex items-center justify-center p-3 sm:p-4'>
        <div className='bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md mx-auto'>
          <div className="text-center mb-4">
            <div className="text-red-500 text-5xl mb-3">⚠️</div>
            <p className='text-red-500 text-sm sm:text-base md:text-lg font-semibold'>
              {error}
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() =>
                navigate(
                  getStorefrontPath({
                    businessName: displayBusinessName,
                    bussinessUuid,
                    suffix: "orders",
                  }),
                )
              }
              className='w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium'
            >
              <MdArrowBack className='text-lg' />
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className='min-h-screen bg-gray-100 flex items-center justify-center p-3 sm:p-4'>
        <div className='bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md mx-auto text-center'>
          <div className="text-gray-400 text-5xl mb-3">📄</div>
          <p className='text-gray-600 text-sm sm:text-base md:text-lg'>
            No booking found.
          </p>
          <button
            onClick={() =>
              navigate(
                getStorefrontPath({
                  businessName: displayBusinessName,
                  bussinessUuid,
                  suffix: "orders",
                }),
              )
            }
            className='mt-4 flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors mx-auto'
          >
            <MdArrowBack className='text-lg' />
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadgeColor = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized === "confirmed" || normalized === "completed") return "text-green-600";
    if (normalized === "in progress" || normalized === "in_progress") return "text-blue-600";
    if (normalized === "cancelled") return "text-red-600";
    return "text-yellow-600";
  };

  const formatBookingDate = (dateValue) => {
    if (!dateValue) return "N/A";
    try {
      return formatDate(dateValue);
    } catch (e) {
      return String(dateValue);
    }
  };

  const formatBookingTime = (timeValue) => {
    if (!timeValue) return "N/A";
    if (typeof timeValue === 'string' && timeValue.includes(':')) {
      return timeValue;
    }
    try {
      const date = new Date(timeValue);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
      }
      return String(timeValue);
    } catch (e) {
      return String(timeValue);
    }
  };

  const getServiceDuration = () => {
    console.log("Getting duration from booking:", booking);
    console.log("Service details:", serviceDetails);
    
    // First check if we have duration from service details
    if (serviceDetails) {
      if (serviceDetails.serviceDuration) {
        return `${serviceDetails.serviceDuration} mins`;
      }
      if (serviceDetails.duration) {
        return `${serviceDetails.duration} mins`;
      }
    }
    
    // Then check booking object
    if (booking.duration && booking.duration !== "N/A") {
      return `${booking.duration} mins`;
    }
    if (booking.serviceDuration) {
      return `${booking.serviceDuration} mins`;
    }
    
    return "N/A";
  };

  const hasQrCode = Boolean(qrCodeUrl);
  console.log("Final QR URL in render:", qrCodeUrl);
  console.log("Has QR Code in render:", hasQrCode);

  return (
    <div className='min-h-screen bg-gray-100 p-2 sm:p-4 md:p-6'>
      <div className='max-w-3xl mx-auto'>
        {/* Action buttons */}
        <div className='flex justify-between items-center mb-3 sm:mb-4'>
          {!shouldAutoDownload ? (
            <button
              onClick={() =>
                navigate(
                  getStorefrontPath({
                    businessName: displayBusinessName,
                    bussinessUuid,
                    suffix: "orders",
                  }),
                )
              }
              className='flex items-center gap-1.5 sm:gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors text-xs sm:text-sm md:text-base'
            >
              <MdArrowBack className='text-sm sm:text-base md:text-lg' />
              <span className='hidden xs:inline'>Back to Orders</span>
            </button>
          ) : (
            <div />
          )}
          <div className='flex gap-1.5 sm:gap-2'>
            <button
              onClick={() => downloadInvoice()}
              disabled={downloading}
              className='flex items-center gap-1.5 sm:gap-2 bg-blue-600 text-white hover:bg-blue-700 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-colors disabled:opacity-50 text-xs sm:text-sm md:text-base'
            >
              {downloading ? (
                <>
                  <div className='animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-t-2 border-b-2 border-white'></div>
                  <span className='hidden sm:inline'>Generating...</span>
                  <span className='sm:hidden'>Saving...</span>
                </>
              ) : (
                <>
                  <MdDownload className='text-sm sm:text-base md:text-lg' />
                  <span className='hidden xs:inline'>Download Invoice</span>
                  <span className='xs:hidden'>Invoice</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Invoice */}
        <div
          ref={invoiceRef}
          className='bg-white rounded-lg shadow-lg p-3 sm:p-5 md:p-6 lg:p-8 font-sans'
        >
          {/* Header */}
          <div className='flex flex-col sm:flex-row justify-between items-center border-b border-gray-200 pb-4 sm:pb-5 md:pb-6 mb-4 sm:mb-5 md:mb-6 gap-3 sm:gap-0'>
            <div className='text-center sm:text-left w-full sm:w-auto'>
              <h1 className='text-xl sm:text-2xl font-bold text-gray-800'>SERVICE INVOICE</h1>
              <p className='text-gray-600 mt-0.5 sm:mt-1 text-xs sm:text-sm'>
                Booking #{booking.bookingId || booking._id?.slice(-6) || "N/A"}
              </p>
            </div>

            <div className='my-3 sm:my-0'>
              <div className='rounded-full overflow-hidden flex items-center justify-center bg-gray-100 shadow-md h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12'>
                <img
                  src={
                    activeStore.businesslogo
                      ? (activeStore.businesslogo.includes('http') ? activeStore.businesslogo : AWS_URL + activeStore.businesslogo)
                      : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT3dSEtDvpd9KAn4SdFJVoL0K1lj4Xfc-wVWQ&s"
                  }
                  alt='Logo'
                  className='h-full w-full object-contain'
                  crossOrigin="anonymous"
                />
              </div>
            </div>

            <div className='text-center sm:text-right w-full sm:w-auto'>
              <p className='text-gray-800 font-semibold text-sm sm:text-base'>Dukanbuddy™</p>
              <p className='text-gray-600 text-xs sm:text-sm'>Powered by Dukanbuddy</p>
            </div>
          </div>

          {/* Company and Customer Details */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-6 sm:mb-7 md:mb-8'>
            <div>
              <h2 className='text-base sm:text-lg font-semibold text-gray-800 mb-1.5 sm:mb-2'>
                Service Provider
              </h2>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {store?.businessName || activeStore?.businessName || "Store Name"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {store?.firstLine ? `${store.firstLine}, ${store.pincode}` : "Business Address"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                Phone: {store?.phone || "N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                Email: {store?.email || "N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                GSTIN: {store?.gstNumber || "N/A"}
              </p>
            </div>

            <div>
              <h2 className='text-base sm:text-lg font-semibold text-gray-800 mb-1.5 sm:mb-2'>
                Customer Details
              </h2>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {booking.customerName || booking.name || "N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                Phone: {booking.mobile || booking.phone || "N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                Email: {booking.email || "N/A"}
              </p>
              {booking.address && (
                <p className='text-gray-600 text-xs sm:text-sm'>
                  Address: {booking.address}
                </p>
              )}
            </div>
          </div>

          {/* Booking Details with Date, Time and Duration */}
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-7 md:mb-8'>
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-1">
                <MdCalendarToday className="text-blue-600 text-sm" />
                <span className="font-semibold text-gray-700 text-xs sm:text-sm">Booking Date</span>
              </div>
              <p className="text-gray-800 text-sm sm:text-base font-medium">
                {formatBookingDate(booking.bookingDate || booking.date)}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-1">
                <MdAccessTime className="text-blue-600 text-sm" />
                <span className="font-semibold text-gray-700 text-xs sm:text-sm">Booking Time</span>
              </div>
              <p className="text-gray-800 text-sm sm:text-base font-medium">
                {formatBookingTime(booking.bookingTime || booking.time)}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 mb-1">
                <MdTimer className="text-blue-600 text-sm" />
                <span className="font-semibold text-gray-700 text-xs sm:text-sm">Duration</span>
              </div>
              <p className="text-gray-800 text-sm sm:text-base font-medium">
                {getServiceDuration()}
              </p>
            </div>
          </div>

          {/* Service Details */}
          <div className='mb-6 sm:mb-7 md:mb-8'>
            <h3 className='text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4'>
              Service Details
            </h3>
            <div className='bg-gray-50 rounded-lg overflow-hidden'>
              <table className='w-full'>
                <thead className='bg-gray-100'>
                  <tr>
                    <th className='py-2 sm:py-3 px-3 sm:px-4 text-left font-semibold text-gray-700 text-xs sm:text-sm'>
                      Service
                    </th>
                    <th className='py-2 sm:py-3 px-3 sm:px-4 text-center font-semibold text-gray-700 text-xs sm:text-sm'>
                      Duration
                    </th>
                    <th className='py-2 sm:py-3 px-3 sm:px-4 text-center font-semibold text-gray-700 text-xs sm:text-sm'>
                      Expert
                    </th>
                    <th className='py-2 sm:py-3 px-3 sm:px-4 text-right font-semibold text-gray-700 text-xs sm:text-sm'>
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className='py-2 sm:py-3 px-3 sm:px-4 border-b text-xs sm:text-sm'>
                      <div>
                        <p className='font-medium text-gray-800'>{booking.serviceName || "Service"}</p>
                        {booking.serviceDescription && (
                          <p className='text-gray-600 text-[10px] sm:text-xs mt-0.5'>
                            {booking.serviceDescription}
                          </p>
                        )}
                        {serviceDetails && serviceDetails.serviceDescription && (
                          <p className='text-gray-600 text-[10px] sm:text-xs mt-0.5 text-blue-600'>
                            {serviceDetails.serviceDescription}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className='py-2 sm:py-3 px-3 sm:px-4 text-center border-b text-xs sm:text-sm'>
                      {getServiceDuration()}
                    </td>
                    <td className='py-2 sm:py-3 px-3 sm:px-4 text-center border-b text-xs sm:text-sm'>
                      {booking.employeeName || "Will be assigned"}
                    </td>
                    <td className='py-2 sm:py-3 px-3 sm:px-4 text-right border-b text-xs sm:text-sm'>
                      ₹{Number(booking.amount || 0).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Service Professional and Additional Details */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-7 md:mb-8'>
            <div>
              <h4 className='font-semibold text-gray-700 text-xs sm:text-sm mb-1.5'>Service Professional</h4>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {booking.employeeName || "Will be assigned"}
              </p>
              {booking.employeeChangeReason && (
                <p className='text-gray-500 text-xs sm:text-sm mt-1'>
                  Note: {booking.employeeChangeReason}
                  {booking.employeeChangedAt && ` (${formatDateTime(booking.employeeChangedAt)})`}
                </p>
              )}
            </div>
            <div>
              <h4 className='font-semibold text-gray-700 text-xs sm:text-sm mb-1.5'>Additional Information</h4>
              {booking.notes && (
                <p className='text-gray-600 text-xs sm:text-sm'>{booking.notes}</p>
              )}
              {booking.customerInstructions && (
                <p className='text-gray-600 text-xs sm:text-sm mt-1'>
                  <span className='font-medium'>Instructions:</span> {booking.customerInstructions}
                </p>
              )}
            </div>
          </div>

          {/* Totals and QR Code Section */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6'>
            <div>
              <h3 className='text-base sm:text-lg font-semibold text-gray-800 mb-1.5 sm:mb-2'>Notes</h3>
              <p className='text-gray-600 text-xs sm:text-sm'>
                Thank you for choosing our service. Please contact us for any queries regarding this invoice.
              </p>
              {booking.status === "completed" && (
                <p className='text-green-600 text-xs sm:text-sm mt-2'>
                  ✓ Service completed on {formatBookingDate(booking.completedAt || booking.updatedAt)}
                </p>
              )}
              
              {/* Show UPI ID if available */}
              {(store?.bussinessUpiQr || activeStore?.bussinessUpiQr) && (
                <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold">UPI ID:</span> {store?.bussinessUpiQr || activeStore?.bussinessUpiQr}
                  </p>
                </div>
              )}
            </div>

            <div>
              <div className='bg-gray-50 rounded-lg p-3 sm:p-4'>
                <div className='flex justify-between py-1.5 sm:py-2'>
                  <span className='text-gray-600 text-xs sm:text-sm'>Service Fee:</span>
                  <span className='text-gray-800 text-xs sm:text-sm'>₹{Number(booking.amount || 0).toFixed(2)}</span>
                </div>

                {booking.taxAmount > 0 && (
                  <div className='flex justify-between py-1.5 sm:py-2'>
                    <span className='text-gray-600 text-xs sm:text-sm'>Tax:</span>
                    <span className='text-gray-800 text-xs sm:text-sm'>₹{Number(booking.taxAmount || 0).toFixed(2)}</span>
                  </div>
                )}

                {booking.discountAmount > 0 && (
                  <div className='flex justify-between py-1.5 sm:py-2'>
                    <span className='text-gray-600 text-xs sm:text-sm'>Discount:</span>
                    <span className='text-green-600 text-xs sm:text-sm'>-₹{Number(booking.discountAmount || 0).toFixed(2)}</span>
                  </div>
                )}

                <div className='flex justify-between py-2 sm:py-3 border-t border-gray-200 mt-1.5 sm:mt-2'>
                  <span className='text-base sm:text-lg font-semibold text-gray-800'>Total:</span>
                  <span className='text-base sm:text-lg font-semibold text-gray-800'>
                    ₹{Number(booking.totalAmount || booking.amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* QR Code Section - Full width below totals */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-2 mb-3">
                <MdQrCode className="text-blue-600 text-xl" />
                <h3 className="text-base font-semibold text-gray-800">Scan to Pay</h3>
              </div>
              {hasQrCode ? (
                <div className="bg-white p-4 rounded-xl shadow-md border border-gray-200">
                  <img
                    src={qrCodeUrl}
                    alt='Payment QR Code'
                    className="w-48 h-48 object-contain"
                    crossOrigin="anonymous"
                    onError={(e) => {
                      console.error("Failed to load QR code from URL:", qrCodeUrl);
                      e.target.style.display = 'none';
                      // Show fallback
                      const parent = e.target.parentNode;
                      const fallback = document.createElement('div');
                      fallback.className = 'text-center p-4';
                      fallback.innerHTML = `
                        <p class="text-red-500 text-sm">Failed to load QR code</p>
                        <p class="text-xs text-gray-400 mt-1">Please contact the business for payment</p>
                        ${(store?.bussinessUpiQr || activeStore?.bussinessUpiQr) ? `<p class="text-xs text-gray-400 mt-1">UPI: ${store?.bussinessUpiQr || activeStore?.bussinessUpiQr}</p>` : ''}
                      `;
                      parent.appendChild(fallback);
                    }}
                  />
                  {(store?.bussinessUpiQr || activeStore?.bussinessUpiQr) && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      UPI ID: {store?.bussinessUpiQr || activeStore?.bussinessUpiQr}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 p-8 rounded-xl border border-gray-200 text-center">
                  <p className="text-gray-500 text-sm">QR Code not available</p>
                  {(store?.bussinessUpiQr || activeStore?.bussinessUpiQr) && (
                    <p className="text-xs text-gray-400 mt-2">
                      UPI ID: {store?.bussinessUpiQr || activeStore?.bussinessUpiQr}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className='mt-8 sm:mt-9 md:mt-10 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200 text-center'>
            <p className='text-gray-600 text-xs sm:text-sm'>Thank you for choosing our service!</p>
            <p className='text-gray-500 text-[10px] sm:text-xs md:text-sm mt-1.5 sm:mt-2'>
              For any queries regarding this invoice, please contact our support team.
            </p>
            <p className='text-gray-500 text-[10px] sm:text-xs md:text-sm mt-3 sm:mt-4'>
              Powered by Dukanbuddy™
            </p>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice, #invoice * {
            visibility: visible;
          }
          #invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
            box-shadow: none;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ServiceInvoicePage;